import type { CreateDriverInput } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Input, Select, Switch } from 'antd';
import type { Dayjs } from 'dayjs';
import { User } from 'lucide-react';

interface CreateDriverFormValues {
  name: string;
  phone: string;
  nationalId: string;
  dateOfBirth: Dayjs;
  licenceNumber: string;
  licenceClasses: string[];
  hazmatCertified: boolean;
}

interface CreateDriverDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateDriverInput) => Promise<boolean>;
  onClose: () => void;
}

function toCreateDriverInput(values: CreateDriverFormValues): CreateDriverInput {
  return {
    name: values.name,
    phone: values.phone,
    nationalId: values.nationalId,
    dateOfBirth: values.dateOfBirth.format('YYYY-MM-DD'),
    licenceNumber: values.licenceNumber,
    licenceClasses: values.licenceClasses,
    hazmatCertified: values.hazmatCertified,
  };
}

/** Name, phone, national ID, licence and hazmat certification — documents are attached from the driver's own detail page after creation. */
export function CreateDriverDrawer({ open, error, submitting, onCreate, onClose }: CreateDriverDrawerProps) {
  const [form] = Form.useForm<CreateDriverFormValues>();

  async function submit(values: CreateDriverFormValues) {
    const created = await onCreate(toCreateDriverInput(values));
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create driver" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateDriverFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ hazmatCertified: false }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input prefix={<User className="size-4 text-muted" />} autoFocus />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Phone"
          rules={[{ required: true, pattern: /^\+254(7|1)\d{8}$/, message: 'Expected a Kenyan mobile number in +254 format' }]}
        >
          <Input placeholder="+254712345678" />
        </Form.Item>
        <Form.Item name="nationalId" label="National ID / passport number" rules={[{ required: true, message: 'Enter a national ID or passport number' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="dateOfBirth" label="Date of birth" rules={[{ required: true, message: 'Select a date of birth' }]}>
          <DatePicker className="w-full" />
        </Form.Item>
        <Form.Item name="licenceNumber" label="Licence number" rules={[{ required: true, message: 'Enter a licence number' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="licenceClasses" label="Licence class(es)" rules={[{ required: true, message: 'Enter at least one licence class' }]}>
          <Select mode="tags" placeholder="e.g. BCE" />
        </Form.Item>
        <Form.Item name="hazmatCertified" label="Hazardous materials certified" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create driver
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
